import { Ambulance, ArrowRight, Clock, ClockAlert, File, Fuel, ImageOff, Info, ListChecks, MapPinned, PlusSquare, Wrench } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useCallback, useRef, useState } from "react";

import dayjs from "dayjs";

import useRights from "@/shared/api/useRights";
import apiService from "@/api/ApiService";
import { imageUrl } from "@/shared/sharedService/helpers";
import { CircularProgress, Box, List, ListItem, ListItemIcon, ListItemText } from "@mui/material";
import { DocumentsPart } from "./DocumentsPart";
import DeviceService from "./DeviceService";
import DeviceMenu, { DeviceTabType } from "./DeviceMenu";
import DeviceFuel from "./DeviceFuel";
import DeviceRunningHours from "./DeviceRunningHours";

interface Resource {}

export default function DeviceInformation({
  deviceId,
  idType = "number",
  withImage = true,
  injectInfo = [],
}: {
  deviceId: string;
  idType: "number" | "id";
  withImage: boolean;
  injectInfo: { Icon: React.ElementType; title: React.ReactNode; subtitle: string }[];
}) {
  const rights = useRights();
  const [visiblePage, setVisiblePage] = useState<DeviceTabType>("INFO");
  const isService = "SERVICE" in rights || "DISPO" in rights;
  const query = useQuery<Resource[], any, Resource | null>({
    queryKey: idType === "id" ? ["getDeviceById", deviceId] : ["getDeviceByNumber", deviceId],
    queryFn:
      idType === "id"
        ? apiService.getEntity("resource", parseInt(deviceId), true, "details")
        : apiService.getEntitiesWithFilter("resource", { number: deviceId }, "details"),
    select: idType === "id" ? (x) => x : (response) => (!!response && response.length > 0 ? response[0] : null),
  });

  const image = !withImage || !query.isSuccess || !query.data?.image ? null : imageUrl(query.data?.image);

  if (query.isError && query.error.status === 401) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {query.isLoadingError ? (
        <Box sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "center" }}>Fehler</Box>
      ) : query.isLoading || !query.data ? (
        <Box sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      ) : (
        <Fragment>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {visiblePage === "INFO" ? (
              <Fragment>
                <DeviceMenu image={image} type={visiblePage} setType={setVisiblePage} />

                <List
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    overflow: "hidden",
                    p: 0,
                  }}
                >
                  <ListItem>
                    <ListItemIcon>
                      <Info />
                    </ListItemIcon>
                    <ListItemText primary={query.data.name} secondary={query.data.number} />
                  </ListItem>
                  {injectInfo.map((k, i) => (
                    <ListItem key={i}>
                      <ListItemIcon>
                        <k.Icon />
                      </ListItemIcon>
                      <ListItemText primary={k.title || undefined} secondary={k.subtitle || undefined} />
                    </ListItem>
                  ))}

                  {!!query.data.lastBooking ? (
                    query.data.lastBooking[0] === "rb" ? (
                      <ListItem>
                        <ListItemIcon>
                          <MapPinned />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            !query.data.lastBooking[1]
                              ? undefined
                              : `${query.data.lastBooking[1].job.phase.project.name} (${query.data.lastBooking[1].job.phase.project.number})`
                          }
                          secondary={
                            !query.data.lastBooking[1]
                              ? "bislang ohne Planung"
                              : dayjs(query.data.lastBooking[1].job.date).calendar(null, {
                                  sameDay: "[Heute]",
                                  lastDay: "[Gestern]",
                                  nextDay: "[]",
                                  lastWeek: "[letzten] dddd",
                                  nextWeek: "[]",
                                  sameElse: "[am] DD.MM.YYYY",
                                })
                          }
                        />
                      </ListItem>
                    ) : query.data.lastBooking[0] === "spb" ? (
                      <ListItem>
                        <ListItemIcon>
                          <MapPinned />
                        </ListItemIcon>
                        <ListItemText
                          primary={query.data.lastBooking[1].specialPlace.name}
                          secondary={
                            "seit " +
                            dayjs(query.data.lastBooking[1].date).calendar(null, {
                              sameDay: "[heute]",
                              lastDay: "[gestern]",
                              nextDay: "[]",
                              lastWeek: "dddd",
                              nextWeek: "[]",
                              sameElse: "DD.MM.YYYY",
                            })
                          }
                        />
                      </ListItem>
                    ) : (
                      <ListItem>
                        <ListItemIcon>
                          <MapPinned />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            !query.data.lastBooking[1]
                              ? undefined
                              : `${query.data.lastBooking[1].project.name} (${query.data.lastBooking[1].project.number})`
                          }
                          secondary={
                            !query.data.lastBooking[1]
                              ? "bislang ohne Planung"
                              : "seit " +
                                dayjs(query.data.lastBooking[1].date).calendar(null, {
                                  sameDay: "[heute]",
                                  lastDay: "[gestern]",
                                  nextDay: "[]",
                                  lastWeek: "dddd",
                                  nextWeek: "[]",
                                  sameElse: "DD.MM.YYYY",
                                })
                          }
                        />
                      </ListItem>
                    )
                  ) : null}

                  <ListItem>
                    <ListItemIcon>
                      <Clock />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        !!query.data.lastMeterReading
                          ? query.data.lastMeterReading.runningHours.toLocaleString("de-DE", {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            }) + " h"
                          : undefined
                      }
                      secondary={
                        !query.data.lastMeterReading
                          ? "Kein Zählerstand bekannt"
                          : `erfasst ${dayjs
                              .utc(query.data.lastMeterReading.createdAt)
                              .local()
                              .fromNow()} von ${query.data.lastMeterReading.createdBy.firstName} ${query.data.lastMeterReading.createdBy.name}`
                      }
                    />
                  </ListItem>

                  {!!query.data.lastFuelingEvent ? (
                    <ListItem>
                      <ListItemIcon>
                        <Fuel />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${Math.round(query.data.lastFuelingEvent.liters)} l ${dayjs
                          .utc(query.data.lastFuelingEvent.createdAt)
                          .local()
                          .fromNow()}`}
                        secondary={`erfasst von ${query.data.lastFuelingEvent.createdBy.firstName} ${query.data.lastFuelingEvent.createdBy.name}`}
                      />
                    </ListItem>
                  ) : (
                    <ListItem>
                      <ListItemIcon>
                        <Fuel />
                      </ListItemIcon>
                      <ListItemText primary={undefined} secondary="Keine Tankung erfasst" />
                    </ListItem>
                  )}

                  {!!query.data.nextMaintenanceEvent &&
                  !!query.data.nextMaintenanceEvent.dueAt &&
                  dayjs(query.data.nextMaintenanceEvent.dueAt, "YYYY-MM-DD").diff(undefined, "day") < 2 ? (
                    <ListItem>
                      <ListItemIcon>
                        {dayjs(query.data.nextMaintenanceEvent.dueAt, "YYYY-MM-DD") <= dayjs() ? <ClockAlert /> : <ListChecks />}
                      </ListItemIcon>
                      <ListItemText
                        primary="Regelmäßige Wartung"
                        secondary={
                          dayjs(query.data.nextMaintenanceEvent.dueAt, "YYYY-MM-DD") <= dayjs()
                            ? "überfällig seit " +
                              dayjs(query.data.nextMaintenanceEvent.dueAt, "YYYY-MM-DD").calendar(null, {
                                sameDay: "[Heute]",
                                lastDay: "[Gestern]",
                                nextDay: "[]",
                                lastWeek: "[letzten] dddd",
                                nextWeek: "[]",
                                sameElse: "DD.MM.YYYY",
                              })
                            : dayjs(query.data.nextMaintenanceEvent.dueAt, "YYYY-MM-DD").format("[fällig am] dddd")
                        }
                      />
                    </ListItem>
                  ) : null}

                  <ListItem>
                    <ListItemIcon>
                      <Ambulance />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        query.data.openDamageCount === 1
                          ? "1 offener Mängelbericht"
                          : query.data.openDamageCount > 1
                            ? `${query.data.openDamageCount} offene Mängelberichte`
                            : undefined
                      }
                      secondary={query.data.openDamageCount === 0 ? "Keine offenen Mängelberichte" : undefined}
                    />
                  </ListItem>
                </List>
              </Fragment>
            ) : visiblePage === "DOCS" ? (
              <DocumentsPart
                image={image}
                title="Dokumente"
                allowCreation={isService}
                parentKey="resource"
                setVisiblePage={setVisiblePage}
                invalidationId={query.data.number}
                parentId={query.data.id}
                files={query.data.files}
              />
            ) : visiblePage === "SERVICE" ? (
              <DeviceService
                image={image}
                setVisiblePage={setVisiblePage}
                readOnly={!isService}
                deviceNumber={query.data.number}
                deviceId={query.data.id}
              />
            ) : visiblePage === "FUEL" ? (
              <DeviceFuel image={image} setVisiblePage={setVisiblePage} deviceId={query.data.id} />
            ) : visiblePage === "HOURS" ? (
              <DeviceRunningHours image={image} setVisiblePage={setVisiblePage} deviceId={query.data.id} deviceNumber={query.data.number} />
            ) : null}
          </Box>
        </Fragment>
      )}
    </Box>
  );
}
